import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Chip, CircularProgress, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';

import CopyableField from '~/components/Common/CopyableField';

import type { InfiniteLinkedMember } from './infiniteLinkedMembers';

function DetailField({
  label,
  value,
  copyValue,
}: {
  label: string;
  value: React.ReactNode;
  copyValue?: string | number | null;
}) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {copyValue ? (
        <CopyableField variant="body1" value={copyValue} label={value} />
      ) : typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body1">{value}</Typography>
      ) : (
        value
      )}
    </Box>
  );
}

function CustomerLink({ customerId, children }: { customerId: string; children: React.ReactNode }) {
  return (
    <MuiLink component={Link} to={`/users/${customerId}`} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </MuiLink>
  );
}

function LinkedMemberCard({ member }: { member: InfiniteLinkedMember }) {
  const nameValue = member.customerId ? (
    <CustomerLink customerId={member.customerId}>{member.name}</CustomerLink>
  ) : (
    member.name
  );

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" color="success" label={member.role} />
          {member.relationship && <Chip size="small" variant="outlined" label={member.relationship} />}
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <DetailField label="Name" value={nameValue} copyValue={member.name} />
          <DetailField label="Email" value={member.email} copyValue={member.email} />
          <DetailField label="Phone" value={member.phone || '—'} copyValue={member.phone} />
        </Box>
      </Stack>
    </Paper>
  );
}

export default function InfiniteLinkedMembersSection({
  isPrimaryProfile,
  members,
  isLoading,
  isError,
}: {
  isPrimaryProfile: boolean;
  members: Array<InfiniteLinkedMember>;
  isLoading: boolean;
  isError: boolean;
}) {
  const heading = isPrimaryProfile
    ? `Secondary travellers (${isLoading ? '…' : members.length})`
    : 'Primary Infinite member';
  const emptyMessage = isPrimaryProfile ? 'No secondary travellers linked.' : 'No primary Infinite member linked.';
  const errorMessage = isPrimaryProfile
    ? 'Could not load secondary travellers.'
    : 'Could not load the primary Infinite member.';

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {heading}
      </Typography>
      {isLoading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading linked traveller details
          </Typography>
        </Stack>
      ) : isError ? (
        <Alert severity="warning">{errorMessage}</Alert>
      ) : members.length > 0 ? (
        <Stack spacing={1}>
          {members.map((member) => (
            <LinkedMemberCard key={`${member.role}-${member.customerId ?? member.email}`} member={member} />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      )}
    </Box>
  );
}
